// src/components/dialogs/UserSelectionDialog.tsx
import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";

import {
    Avatar, AvatarFallback, AvatarImage, Button, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, Input, ScrollArea
} from "@/components/ui";
import { useDebounce, useUsers } from "@/hooks";
import type { User } from "@/types/user";

interface UserSelectionDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onUserSelect: (user: User) => void;
}

const ITEMS_PER_PAGE = 20;

export function UserSelectionDialog({
    open,
    onOpenChange,
    onUserSelect,
}: UserSelectionDialogProps) {
    const { t } = useTranslation();
    const [searchQuery, setSearchQuery] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const debouncedSearch = useDebounce(searchQuery, 300);
    const { users, loading } = useUsers(debouncedSearch);

    // Calculate pagination
    const totalPages = Math.ceil(users.length / ITEMS_PER_PAGE);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const paginatedUsers = users.slice(startIndex, startIndex + ITEMS_PER_PAGE);

    // Reset page when search changes
    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearch]);

    const handleUserSelect = (user: User) => {
        onUserSelect(user);
        onOpenChange(false);
    };

    const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
        // Load more when user scrolls to bottom
        if (scrollHeight - scrollTop <= clientHeight * 1.5 && currentPage < totalPages) {
            setCurrentPage(prev => prev + 1);
        }
    }, [currentPage, totalPages]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>{t("header.loginAsClient")}</DialogTitle>
                    <DialogDescription>
                        {t("header.loginAsClientDescription")}
                    </DialogDescription>
                </DialogHeader>
                <div className="py-4">
                    <Input
                        placeholder={t("common.search")}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="mb-4"
                    />
                    <ScrollArea
                        className={`pr-4 transition-all duration-500 ${searchQuery.length >= 2 && paginatedUsers.length > 0 ? 'h-[300px]' : 'h-[80px]'
                            }`}
                        onScroll={handleScroll}
                    >
                        {loading ? (
                            <div className="flex items-center justify-center h-full">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ps-blue-500"></div>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {searchQuery.length < 2 ? (
                                    <div className="text-center text-muted-foreground py-8">
                                        {t("common.typeToSearch", { count: 2 })}
                                    </div>
                                ) : paginatedUsers.length === 0 ? (
                                    <div className="text-center text-muted-foreground py-8">
                                        {t("common.noResults")}
                                    </div>
                                ) : (
                                    <>
                                        {paginatedUsers.map((user) => (
                                            <Button
                                                key={user.id}
                                                variant="ghost"
                                                className="w-full justify-start gap-3 h-auto p-3"
                                                onClick={() => handleUserSelect(user)}
                                            >
                                                <Avatar>
                                                    <AvatarImage src={user.avatar} alt={`${user.firstName} ${user.lastName}`} />
                                                    <AvatarFallback>
                                                        {`${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`}
                                                    </AvatarFallback>
                                                </Avatar>
                                                <div className="flex flex-col items-start">
                                                    <span className="font-medium">
                                                        {`${user.firstName || ''} ${user.lastName || ''}`}
                                                    </span>
                                                    <span className="text-sm text-muted-foreground">
                                                        {user.email}
                                                    </span>
                                                </div>
                                            </Button>
                                        ))}
                                        {currentPage < totalPages && (
                                            <div className="flex justify-center py-2">
                                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-ps-blue-500"></div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </ScrollArea>
                </div>
            </DialogContent>
        </Dialog>
    );
} 